/* global fbq */

import React, { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons'
import LPTooltip from './LPTooltip' // adjust path as needed

export default function PremiumSelect({
  label,                // string label above the box
  labelTooltipText = '',// tooltip text next to the label (optional)
  value,                // current selected value
  onChange,             // callback (newValue) => void
  options,              // array of strings or { value: string, tooltipText?: string }
  className = '',       // extra wrapper classes
}) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  // close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const pushToDataLayer = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  const handleOptionClick = (optValue) => {
    // propagate change
    onChange(optValue)

    // track CustomizeProduct with 30m cooldown
    const last = localStorage.getItem('customizeProductEvent')
    const now = Date.now()
    const cooldown = 30 * 60 * 1000
    if (!last || now - Number(last) >= cooldown) {
      localStorage.setItem('customizeProductEvent', now.toString())
      if (typeof fbq !== 'undefined') {
        fbq('track', 'CustomizeProduct')
      }
      pushToDataLayer({
        event: 'customize_product'
      });
    }

    setOpen(false)
  }

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      {label && (
        <div className="flex items-center mb-1">
          <p className="text-sm text-[var(--secondary-text)]">{label}</p>
          {labelTooltipText && (
            <span className="ml-2 flex-shrink-0">
              <LPTooltip tooltipText={labelTooltipText} />
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="
          w-full rounded-md border border-[var(--border)] bg-[var(--bg-surface)]
          p-2 flex justify-between items-center text-white
          focus:outline-none focus:ring-1 focus:ring-[var(--focus-ring)]
          shadow-sm
        "
      >
        <span>{value}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`text-white transition-transform duration-200 ease-in-out ${open ? 'rotate-180' : 'rotate-0'}`}
        />
      </button>

      {open && (
        <ul className="
          absolute mt-1 w-full max-h-60 overflow-auto rounded-md
          bg-[var(--bg-surface)] py-1 text-base shadow-lg
          border border-[var(--border)] ring-1 ring-[var(--focus-ring)]
          scrollbar-custom z-10
        ">
          {options.map((opt) => {
            const { value: optValue, tooltipText } =
              typeof opt === 'object' && opt.value
                ? opt
                : { value: opt, tooltipText: null }
            const selected = optValue === value

            return (
              <li
                key={optValue}
                onClick={() => handleOptionClick(optValue)}
                className={`
                  cursor-pointer select-none px-4 py-2 flex justify-between items-center
                  ${selected ? 'font-semibold text-[var(--blue)]' : 'text-[#b3b3b3]'}
                  hover:bg-[var(--bg-surface-variant)] hover:text-white
                `}
              >
                <div className="flex items-center">
                  <span>{optValue}</span>
                  {tooltipText && (
                    <span className="ml-2 flex-shrink-0">
                      <LPTooltip tooltipText={tooltipText} />
                    </span>
                  )}
                </div>
                {selected && <FontAwesomeIcon icon={faCheck} className="text-[var(--blue)]" />}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
